import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Mic, Search, ChevronRight } from 'lucide-react';
import { EmptyState } from './EmptyState';
interface Turn {
  speaker: 'matt' | 'zenbot';
  text: string;
  time: string;
}
interface Transcript {
  id: string;
  title: string;
  date: string;
  duration: string;
  turnCount: number;
  summary: string;
  tags: string[];
  turns: Turn[];
}
const TRANSCRIPTS: Transcript[] = [{
  id: 't1',
  title: 'Q2 budget walkthrough',
  date: 'Today, 9:02 AM',
  duration: '4:12',
  turnCount: 18,
  summary: 'Reviewed Sarah\u2019s revised Q2 numbers, approved Lisa\u2019s SSL PR, queued follow-up on Acme contract.',
  tags: ['Work', 'Finance'],
  turns: [{
    speaker: 'zenbot',
    text: 'Good morning, Matt. You have 3 priority emails and a meeting at 11. Want me to run through them?',
    time: '9:02'
  }, {
    speaker: 'matt',
    text: 'Yes, start with the emails.',
    time: '9:02'
  }, {
    speaker: 'zenbot',
    text: 'Sarah needs Q2 budget approval. James sent the Acme Corp contract for review. Lisa flagged that the server certificate expires Friday.',
    time: '9:02'
  }, {
    speaker: 'matt',
    text: 'Approve Lisa\u2019s PR and tell Sarah I\u2019ll review the budget by end of day.',
    time: '9:03'
  }, {
    speaker: 'zenbot',
    text: 'Done. I\u2019ve approved Lisa\u2019s PR and sent Sarah a message letting her know you\u2019ll review by EOD.',
    time: '9:03'
  }]
}, {
  id: 't2',
  title: 'Rate limiting discussion with Tom',
  date: 'Yesterday, 2:14 PM',
  duration: '7:45',
  turnCount: 24,
  summary: 'Debated token-bucket vs sliding window for /sync endpoint. Agreed on token-bucket with burst allowance. Broken down into research, draft, and reply tasks.',
  tags: ['Engineering'],
  turns: [{
    speaker: 'matt',
    text: 'Tom\u2019s been asking about rate limiting the sync endpoint. Walk me through the options.',
    time: '14:14'
  }, {
    speaker: 'zenbot',
    text: 'Two main approaches: token-bucket with burst allowance, or sliding window. Token-bucket is simpler to implement at the gateway and handles bursty traffic better.',
    time: '14:14'
  }]
}, {
  id: 't3',
  title: 'Morning briefing',
  date: 'Yesterday, 8:58 AM',
  duration: '3:02',
  turnCount: 12,
  summary: 'Quick rundown of overnight Slack mentions, calendar for the day, and outstanding tasks.',
  tags: ['Work'],
  turns: []
}, {
  id: 't4',
  title: 'Acme contract review',
  date: '2 days ago, 4:30 PM',
  duration: '12:08',
  turnCount: 42,
  summary: 'Walked through Acme contract section by section. Flagged liability clause in 4.2 for legal review.',
  tags: ['Work', 'Legal'],
  turns: []
}, {
  id: 't5',
  title: 'Walk to the cafe',
  date: '3 days ago, 11:15 AM',
  duration: '6:33',
  turnCount: 19,
  summary: 'Captured ideas about zenbot task breakdown UI. Discussed tool badges and structured actions pattern.',
  tags: ['Ideas', 'Product'],
  turns: []
}];
export function TranscriptsView() {
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);
  if (viewingId) {
    const t = TRANSCRIPTS.find(x => x.id === viewingId);
    if (t) return <TranscriptDetail transcript={t} onBack={() => setViewingId(null)} data-id="element-2276" />;
  }
  const filtered = TRANSCRIPTS.filter(t => !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.summary.toLowerCase().includes(search.toLowerCase()));
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-2277">
      <div className="max-w-[640px] mx-auto w-full flex flex-col" data-id="element-2278">
        <header className="mb-6" data-id="element-2279">
          <h1 className="text-[20px] font-bold text-[#e8e4df] tracking-tight mb-1" data-id="element-2280">Transcripts</h1>
          <p className="text-[#8a8680] text-[14px]" data-id="element-2281">Past voice sessions with zenbot.</p>
        </header>

        <div className="relative mb-6" data-id="element-2282">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8a8680]/50" data-id="element-2283" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transcripts..." className="w-full bg-[#1c1c22] text-[#e8e4df] rounded-[12px] pl-11 pr-4 py-3 text-[15px] outline-none placeholder:text-[#8a8680]/40 focus:ring-1 focus:ring-[#4a9a9a]/30" data-id="element-2284" />
        </div>

        {empty || filtered.length === 0 ? <EmptyState message={empty ? 'No sessions yet.' : 'No transcripts match.'} sub={empty ? 'Start talking to zenbot and your sessions will appear here.' : undefined} data-id="element-2285" /> : <div className="flex flex-col gap-3" data-id="element-2286">
            {filtered.map((t, i) => <motion.button key={t.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: i * 0.04
        }} onClick={() => setViewingId(t.id)} className="bg-[#1c1c22] rounded-[16px] p-5 shadow-xl shadow-black/20 text-left group hover:bg-[#1e1e25] transition-colors w-full focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40" data-id="element-2287">
                <div className="flex items-start gap-4" data-id="element-2288">
                  <div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0 mt-0.5" data-id="element-2289"><Mic size={16} className="text-[#8a8680]" data-id="element-2290" /></div>
                  <div className="flex-1 min-w-0" data-id="element-2291">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-2292">
                      <h2 className="text-[#e8e4df] font-medium text-[15px]" data-id="element-2293">{t.title}</h2>
                      <span className="text-[11px] text-[#8a8680]/40 flex items-center gap-1" data-id="element-2294"><Clock size={10} data-id="element-2295" />{t.duration}</span>
                    </div>
                    <p className="text-[#8a8680] text-[13px] mt-1 leading-[1.6] line-clamp-2" data-id="element-2296">{t.summary}</p>
                    <div className="flex items-center gap-2 mt-2" data-id="element-2297">
                      {t.tags.map(tag => <span key={tag} className="text-[10px] text-[#8a8680]/50 bg-[#252530] px-2 py-0.5 rounded-full" data-id="element-2298">{tag}</span>)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0" data-id="element-2299">
                    <span className="text-[12px] text-[#8a8680]/40 whitespace-nowrap" data-id="element-2300">{t.date}</span>
                    <span className="text-[11px] text-[#8a8680]/30" data-id="element-2301">{t.turnCount} turns</span>
                  </div>
                </div>
              </motion.button>)}
          </div>}

        <button onClick={() => setEmpty(!empty)} className="fixed bottom-4 right-20 text-[10px] text-[#8a8680]/30 hover:text-[#8a8680] bg-[#1c1c22]/60 backdrop-blur-sm px-2 py-1 rounded-full" data-id="element-2302">{empty ? 'Restore demo' : 'Empty demo'}</button>
      </div>
    </div>;
}
function TranscriptDetail({
  transcript,
  onBack
}: {
  transcript: Transcript;
  onBack: () => void;
}) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.3
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] selection:bg-[#4a9a9a]/30 pt-12 pb-32 px-6" data-id="element-2303">
      <div className="max-w-[680px] mx-auto w-full" data-id="element-2304">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680] hover:text-[#e8e4df] transition-colors mb-8 focus:outline-none focus:ring-2 focus:ring-[#4a9a9a]/40 rounded" data-id="element-2305"><ArrowLeft size={16} data-id="element-2306" /><span className="text-sm" data-id="element-2307">Transcripts</span></button>
        <header className="mb-8" data-id="element-2308">
          <h1 className="text-[22px] font-bold text-[#e8e4df] tracking-tight" data-id="element-2309">{transcript.title}</h1>
          <div className="flex items-center gap-3 mt-2" data-id="element-2310">
            <span className="text-[13px] text-[#8a8680]/50" data-id="element-2311">{transcript.date}</span>
            <span className="text-[#8a8680]/20" data-id="element-2312">&middot;</span>
            <span className="text-[13px] text-[#8a8680]/50 flex items-center gap-1" data-id="element-2313"><Clock size={11} data-id="element-2314" />{transcript.duration}</span>
            <span className="text-[#8a8680]/20" data-id="element-2315">&middot;</span>
            <span className="text-[13px] text-[#8a8680]/50" data-id="element-2316">{transcript.turnCount} turns</span>
          </div>
        </header>

        <div className="bg-[#1c1c22] rounded-[16px] p-6 shadow-xl shadow-black/20 mb-6" data-id="element-2317">
          <h3 className="text-xs font-semibold text-[#8a8680] uppercase tracking-wider mb-3" data-id="element-2318">Summary</h3>
          <p className="text-[#e8e4df]/80 text-[15px] leading-[1.7]" data-id="element-2319">{transcript.summary}</p>
        </div>

        {transcript.turns.length > 0 && <div data-id="element-2320">
            <h3 className="text-xs font-semibold text-[#8a8680] uppercase tracking-wider mb-4 px-1" data-id="element-2321">Conversation</h3>
            <div className="flex flex-col gap-4" data-id="element-2322">
              {transcript.turns.map((turn, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3,
            delay: i * 0.04
          }} className={`flex ${turn.speaker === 'matt' ? 'justify-end' : 'justify-start'}`} data-id="element-2323">
                  <div className={`max-w-[85%] rounded-[16px] px-5 py-3 ${turn.speaker === 'matt' ? 'bg-[#252530]' : 'bg-[#1c1c22]'}`} data-id="element-2324">
                    <div className="flex items-center gap-2 mb-1" data-id="element-2325">
                      <span className="text-[11px] text-[#8a8680]/50 uppercase tracking-wider" data-id="element-2326">{turn.speaker === 'matt' ? 'Matt' : 'Zenbot'}</span>
                      <span className="text-[11px] text-[#8a8680]/30" data-id="element-2327">{turn.time}</span>
                    </div>
                    <p className="text-[#e8e4df]/90 text-[14px] leading-[1.6]" data-id="element-2328">{turn.text}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>}
      </div>
    </motion.div>;
}